import { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  message,
} from 'antd';
import { useDispatch } from 'react-redux';
import { HolderOutlined } from '@ant-design/icons';
import { createArea, updateArea } from '../../../redux/slices/areas';
import Close from '../../../components/Close';
import styles from '../styles/Editor.module.css';

const { TextArea } = Input;

const FALLBACK_SERVICE_CATALOG = [
  {
    key: 'DRIVER',
    title: 'Drivers propios',
    subtitle: 'Primero se intenta con motoristas internos'
  },
  {
    key: 'PEYA',
    title: 'PedidosYa',
    subtitle: 'Primero se intenta con courier externo'
  }
];

const PAYMENT_METHOD_OPTIONS = [
  { label: 'Efectivo', value: 'CASH' },
  { label: 'Tarjeta', value: 'CARD' }
];

const PRICE_OPERATOR_OPTIONS = [
  { label: 'Menor o igual a', value: '<' },
  { label: 'Mayor o igual a', value: '>' }
];

const ALLOWED_PAYMENT_METHODS = PAYMENT_METHOD_OPTIONS.map((option) => option.value);

const GENERIC_SERVICE_CONFIG = {
  time_minutes: 3,
  payment_methods: ['CASH', 'CARD'],
  price: 0,
  operator: '<',
  active: true
};

const DEFAULT_SERVICE_CONFIG = {
  DRIVER: {
    ...GENERIC_SERVICE_CONFIG
  },
  PEYA: {
    ...GENERIC_SERVICE_CONFIG
  }
};

const DEFAULT_PRIORITY = FALLBACK_SERVICE_CATALOG.map((service) => service.key);

const normalizeServiceKey = (value) =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_');

const formatServiceTitle = (serviceKey) => {
  const normalized = String(serviceKey || '')
    .toLowerCase()
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(' ');

  return normalized || 'Servicio';
};

const parseJsonValue = (rawValue, fallbackValue) => {
  if (typeof rawValue !== 'string') {
    return rawValue;
  }

  try {
    return JSON.parse(rawValue);
  } catch (error) {
    return fallbackValue;
  }
};

const parseServiceCatalog = (rawValue) => {
  const parsed = parseJsonValue(rawValue, []);

  if (!parsed) {
    return [];
  }

  const list = Array.isArray(parsed)
    ? parsed
    : typeof parsed === 'object'
      ? Object.entries(parsed).map(([key, value]) => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          return { key, ...value };
        }

        return {
          key,
          title: String(value || '').trim()
        };
      })
      : [];

  return list.reduce((acc, entry) => {
    const normalizedEntry = typeof entry === 'string' ? { key: entry } : entry;

    if (!normalizedEntry || typeof normalizedEntry !== 'object') {
      return acc;
    }

    const key = normalizeServiceKey(
      normalizedEntry.key ||
      normalizedEntry.id ||
      normalizedEntry.code ||
      normalizedEntry.value ||
      normalizedEntry.name
    );

    if (!key || acc.some((service) => service.key === key)) {
      return acc;
    }

    const rawTitle = normalizedEntry.title || normalizedEntry.label || normalizedEntry.name;
    const rawSubtitle = normalizedEntry.subtitle || normalizedEntry.description;

    acc.push({
      key,
      title: rawTitle && String(rawTitle).trim() ? String(rawTitle).trim() : formatServiceTitle(key),
      subtitle: rawSubtitle && String(rawSubtitle).trim()
        ? String(rawSubtitle).trim()
        : 'Configura prioridad y reglas de este servicio'
    });

    return acc;
  }, []);
};

const parsePriorityList = (rawValue) => {
  const parsed = parseJsonValue(rawValue, []);

  if (!Array.isArray(parsed)) {
    return [];
  }

  return parsed.reduce((acc, keyValue) => {
    const normalizedKey = normalizeServiceKey(keyValue);

    if (!normalizedKey || acc.includes(normalizedKey)) {
      return acc;
    }

    acc.push(normalizedKey);
    return acc;
  }, []);
};

const parseServiceConfig = (rawValue) => {
  const parsed = parseJsonValue(rawValue, {});

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {};
  }

  return parsed;
};

const normalizeServiceConfigByKey = (rawConfig) =>
  Object.entries(rawConfig || {}).reduce((acc, [rawKey, rawValue]) => {
    const key = normalizeServiceKey(rawKey);

    if (!key || !rawValue || typeof rawValue !== 'object' || Array.isArray(rawValue)) {
      return acc;
    }

    acc[key] = rawValue;
    return acc;
  }, {});

const buildServiceCatalog = (area, normalizedConfig) => {
  const dynamicCatalog = parseServiceCatalog(
    area?.dispatch_services_catalog || area?.dispatch_service_catalog || area?.dispatchServicesCatalog
  );

  const serviceMap = {};
  const orderedKeys = [];

  const addService = (service) => {
    const normalizedKey = normalizeServiceKey(service && service.key);

    if (!normalizedKey || serviceMap[normalizedKey]) {
      return;
    }

    serviceMap[normalizedKey] = {
      key: normalizedKey,
      title: service && service.title ? service.title : formatServiceTitle(normalizedKey),
      subtitle: service && service.subtitle
        ? service.subtitle
        : 'Configura prioridad y reglas de este servicio'
    };
    orderedKeys.push(normalizedKey);
  };

  dynamicCatalog.forEach(addService);
  Object.keys(normalizedConfig || {}).forEach((key) => addService({ key }));
  FALLBACK_SERVICE_CATALOG.forEach(addService);

  return orderedKeys.map((key) => serviceMap[key]);
};

const moveBefore = (list, sourceKey, targetKey) => {
  const fromIndex = list.indexOf(sourceKey);
  const toIndex = list.indexOf(targetKey);

  if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
    return list;
  }

  const next = [...list];
  next.splice(fromIndex, 1);
  const adjustedToIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
  next.splice(adjustedToIndex, 0, sourceKey);
  return next;
};

const dispatchOrderToPriority = (dispatchOrder, serviceKeys) => {
  const basePriority = Array.isArray(serviceKeys) && serviceKeys.length > 0
    ? [...serviceKeys]
    : [...DEFAULT_PRIORITY];

  if (!basePriority.includes('DRIVER') || !basePriority.includes('PEYA')) {
    return basePriority;
  }

  if (dispatchOrder === 'PEYA_FIRST') {
    return moveBefore(basePriority, 'PEYA', 'DRIVER');
  }

  return moveBefore(basePriority, 'DRIVER', 'PEYA');
};

const priorityToDispatchOrder = (priority) =>
  Array.isArray(priority) && priority.indexOf('PEYA') > -1 && priority.indexOf('DRIVER') > -1
    ? priority.indexOf('PEYA') < priority.indexOf('DRIVER')
      ? 'PEYA_FIRST'
      : 'DRIVER_FIRST'
    : 'DRIVER_FIRST';

const normalizeNumberOrNull = (value) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizePriceOperator = (value, fallback = '<') => {
  const normalized = String(value || '').trim().toUpperCase();

  if (normalized === '<' || normalized === '<=' || normalized === 'LTE' || normalized === 'LE') {
    return '<';
  }

  if (normalized === '>' || normalized === '>=' || normalized === 'GTE' || normalized === 'GE') {
    return '>';
  }

  return fallback;
};

const normalizeServiceActive = (value, fallback = true) => {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }

  if (value && typeof value === 'object') {
    const bufferData = Array.isArray(value.data) ? value.data : null;

    if (bufferData && bufferData.length > 0) {
      return Number.parseInt(bufferData[0], 10) === 1;
    }
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  const normalized = String(value).trim().toUpperCase();

  if (normalized === '1' || normalized === 'TRUE' || normalized === 'YES' || normalized === 'ON') {
    return true;
  }

  if (normalized === '0' || normalized === 'FALSE' || normalized === 'NO' || normalized === 'OFF') {
    return false;
  }

  return fallback;
};

const getServiceDefaults = (serviceKey, peyaDelay) => {
  const baseDefaults = DEFAULT_SERVICE_CONFIG[serviceKey] || GENERIC_SERVICE_CONFIG;

  if (serviceKey !== 'PEYA') {
    return { ...baseDefaults };
  }

  return {
    ...baseDefaults,
    time_minutes: peyaDelay
  };
};

const buildInitialServiceConfig = (area, serviceKeys, normalizedConfig) => {
  const peyaDelay = Number.isInteger(area?.peya_delay_minutes) ? area.peya_delay_minutes : 3;
  const keys = Array.isArray(serviceKeys) && serviceKeys.length > 0
    ? serviceKeys
    : DEFAULT_PRIORITY;

  return keys.reduce((acc, key) => {
    const serviceRaw = normalizedConfig[key] || {};
    const serviceDefault = getServiceDefaults(key, peyaDelay);

    const paymentMethods = serviceRaw.payment_methods || serviceRaw.paymentMethods;
    const filteredPayments = Array.isArray(paymentMethods)
      ? paymentMethods.filter((method) => ALLOWED_PAYMENT_METHODS.includes(method))
      : [];
    const normalizedPayments = filteredPayments.length > 0
      ? filteredPayments
      : serviceDefault.payment_methods;

    const timeMinutes = serviceRaw.time_minutes ?? serviceRaw.timeMinutes;
    const price = serviceRaw.price ?? serviceRaw.priceValue;
    const operator = serviceRaw.operator ?? serviceRaw.price_operator ?? serviceRaw.priceOperator;
    const activeRaw = serviceRaw.active ?? serviceRaw.enabled ?? serviceRaw.is_active ?? serviceRaw.isActive;
    const legacyPriceMin = serviceRaw.price_min ?? serviceRaw.priceMin;
    const legacyPriceMax = serviceRaw.price_max ?? serviceRaw.priceMax;
    const parsedLegacyPriceMin = normalizeNumberOrNull(legacyPriceMin);
    const parsedLegacyPriceMax = normalizeNumberOrNull(legacyPriceMax);
    let normalizedPrice = normalizeNumberOrNull(price);
    let normalizedOperator = normalizePriceOperator(operator, serviceDefault.operator);

    if (normalizedPrice === null) {
      if (parsedLegacyPriceMin !== null && parsedLegacyPriceMax === null) {
        normalizedPrice = parsedLegacyPriceMin;
        normalizedOperator = '>';
      } else if (parsedLegacyPriceMax !== null) {
        normalizedPrice = parsedLegacyPriceMax;
        normalizedOperator = '<';
      }
    }

    const parsedTimeMinutes = Number.parseInt(timeMinutes, 10);

    acc[key] = {
      time_minutes: Number.isInteger(timeMinutes)
        ? timeMinutes
        : Number.isInteger(parsedTimeMinutes)
          ? parsedTimeMinutes
          : serviceDefault.time_minutes,
      payment_methods: normalizedPayments,
      price: normalizedPrice !== null && normalizedPrice >= 0
        ? normalizedPrice
        : serviceDefault.price,
      operator: normalizePriceOperator(normalizedOperator, serviceDefault.operator),
      active: normalizeServiceActive(activeRaw, serviceDefault.active)
    };

    return acc;
  }, {});
};

const Editor = ({ close, area, handleArea }) => {
  const [sending, handleSending] = useState(false);
  const [dispatchServices, setDispatchServices] = useState(FALLBACK_SERVICE_CATALOG);
  const [dispatchPriority, setDispatchPriority] = useState(DEFAULT_PRIORITY);
  const [draggingKey, setDraggingKey] = useState(null);
  const [draggedOverKey, setDraggedOverKey] = useState(null);
  const statusLabel = area && (area.active ?? area.activo) === 0 ? 'Inactivo' : 'Activo';
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const dispatchServiceMap = dispatchServices.reduce((acc, service) => {
    acc[service.key] = service;
    return acc;
  }, {});

  useEffect(() => {
    const rawConfig = normalizeServiceConfigByKey(
      parseServiceConfig(
        area?.dispatch_services || area?.dispatch_services_config || area?.dispatchServiceConfig
      )
    );

    const catalog = buildServiceCatalog(area, rawConfig);
    const catalogKeys = catalog.map((service) => service.key);
    const explicitPriority = parsePriorityList(
      area?.dispatch_services_priority || area?.dispatchServicesPriority
    );
    const mergedPriority = [
      ...explicitPriority.filter((key) => catalogKeys.includes(key)),
      ...catalogKeys.filter((key) => !explicitPriority.includes(key))
    ];
    const initialConfig = buildInitialServiceConfig(area, catalogKeys, rawConfig);

    setDispatchServices(catalog);
    setDispatchPriority(dispatchOrderToPriority(area?.dispatch_order, mergedPriority));

    if (area) {
      form.setFieldsValue({
        nombre: area.nombre,
        descripcion: area.descripcion || '',
        dispatch_services: initialConfig,
      });
      return;
    }

    form.setFieldsValue({
      dispatch_services: initialConfig,
    });
  }, [area, form]);

  const handleDragOver = (event, key) => {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    if (draggedOverKey !== key) {
      setDraggedOverKey(key);
    }
  };

  const handleDragStart = (event, key) => {
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
    setDraggingKey(key);
  };

  const handleDragEnd = () => {
    setDraggingKey(null);
    setDraggedOverKey(null);
  };

  const handleDragLeave = (key) => {
    if (draggedOverKey === key) {
      setDraggedOverKey(null);
    }
  };

  const handleDrop = (targetKey) => {
    setDraggedOverKey(null);
    if (!draggingKey || draggingKey === targetKey) {
      setDraggingKey(null);
      return;
    }

    setDispatchPriority((current) => {
      const next = [...current];
      const fromIndex = next.indexOf(draggingKey);
      const toIndex = next.indexOf(targetKey);

      if (fromIndex === -1 || toIndex === -1) {
        return current;
      }

      next.splice(fromIndex, 1);
      next.splice(toIndex, 0, draggingKey);
      return next;
    });

    setDraggingKey(null);
  };

  const save = async () => {
    try {
      handleSending(true);
      const values = await form.validateFields();
      const rawServicesConfig = normalizeServiceConfigByKey(values.dispatch_services || {});
      const orderedServiceKeys = dispatchPriority.length > 0
        ? dispatchPriority
        : dispatchServices.map((service) => service.key);
      const servicesConfig = buildInitialServiceConfig(area, orderedServiceKeys, rawServicesConfig);
      const peyaService = servicesConfig.PEYA || {};
      const fallbackPeyaDelay = Number.isInteger(area?.peya_delay_minutes) ? area.peya_delay_minutes : 3;
      const info = {
        nombre: values.nombre,
        descripcion: values.descripcion,
        active: area ? (area.active ?? area.activo ?? 1) : 1,
        dispatch_order: priorityToDispatchOrder(dispatchPriority),
        peya_delay_minutes: Number.isInteger(peyaService.time_minutes)
          ? peyaService.time_minutes
          : fallbackPeyaDelay,
        dispatch_services: servicesConfig,
        dispatch_services_priority: orderedServiceKeys,
        dispatch_services_catalog: dispatchServices,
      };
      
      if (area) {
        info.id = area.id;
        const response = await dispatch(updateArea(info));
        if (response.status === 'success') {
          handleSending(false);
          message.success('Área actualizada con éxito');
          close();
        } else {
          handleSending(false);
          message.error('¡Hubo un problema! Inténtalo de nuevo');
        }
      } else {
        const response = await dispatch(createArea(info));
        if (response.status === 'success') {
          message.success('Área creada con éxito');
          handleSending(false);
          if (handleArea) {
            handleArea(response.area);
          }
          close();
        } else {
          handleSending(false);
          message.error('¡Hubo un problema! Inténtalo de nuevo');
        }
      }
    } catch (e) {
      handleSending(false);
      message.error('Error al procesar la solicitud');
    }
  };

  return (
    <div className={styles.editor}>
      <div className={styles.header}>
        <span className={styles.title}>
          {area ? 'Editar Área' : 'Nueva Área'}
        </span>
        <Close action={close} />
      </div>
      <Form
        name="area-editor"
        autoComplete="off"
        form={form}
        colon={false}
        requiredMark={false}
        onFinish={save}
      > 
        <Row>
          <Col span={24}>
            <Form.Item
              label={<span className={styles.label}>NOMBRE</span>}
              name="nombre"
              className={styles.itemColumn}
              rules={[{required: true, message: 'Ingresar el nombre del área'}]}
            >
              <Input size="large" className={styles.input} placeholder="Nombre del área" />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item
              label={
                <span className={styles.label}>
                  DESCRIPCIÓN
                </span>
              }
              name="descripcion"
              className={styles.itemColumn}
            >
              <TextArea 
                size="large" 
                className={styles.input} 
                rows={4}
                placeholder="Descripción del área (opcional)"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item
              label={<span className={styles.label}>ORDEN DE DESPACHO</span>}
              className={`${styles.itemColumn} ${styles.dispatchOrderItem}`}
            >
              
              <div className={styles.dragList}>
                {dispatchPriority.map((key, index) => {
                  const item = dispatchServiceMap[key] || {
                    key,
                    title: formatServiceTitle(key),
                    subtitle: 'Configura prioridad y reglas de este servicio'
                  };
                  const dragging = draggingKey === key;

                  return (
                    <div
                      key={key}
                      onDragOver={(event) => handleDragOver(event, key)}
                      onDragLeave={() => handleDragLeave(key)}
                      onDrop={() => handleDrop(key)}
                      className={`${styles.dragItem} ${dragging ? styles.dragItemDragging : ''} ${draggedOverKey === key && !dragging ? styles.dragItemDragOver : ''}`}
                    >
                      <span className={styles.dragIndex}>{index + 1}</span>
                      <div className={styles.dragBody}>
                        <div className={styles.dragTopRow}>
                          <div className={styles.dragTopText}>
                            <div className={styles.dragTitle}>{item.title}</div>
                            <div className={styles.dragSubtitle}>{item.subtitle}</div>
                          </div>
                          <div className={styles.serviceTopActions}>
                            <Form.Item
                              name={['dispatch_services', key, 'active']}
                              valuePropName="checked"
                              className={styles.serviceToggleItem}
                            >
                              <Switch
                                size="small"
                                checkedChildren="Activo"
                                unCheckedChildren="Inactivo"
                              />
                            </Form.Item>

                            <span
                              className={styles.dragHandle}
                              draggable
                              onDragStart={(event) => handleDragStart(event, key)}
                              onDragEnd={handleDragEnd}
                            >
                              <HolderOutlined />
                            </span>
                          </div>
                        </div>

                        <div className={styles.serviceConfigGrid}>
                          <Form.Item
                            label={<span className={styles.fieldLabel}>Tiempo (min)</span>}
                            name={['dispatch_services', key, 'time_minutes']}
                            className={styles.serviceField}
                            rules={[
                              { required: true, message: 'Ingresa un tiempo' },
                              { type: 'number', min: 0, max: 120, message: 'Usa un valor entre 0 y 120' },
                            ]}
                          >
                            <InputNumber
                              size="middle"
                              min={0}
                              max={120}
                              style={{ width: '100%' }}
                              placeholder="Ej: 3"
                            />
                          </Form.Item>

                          <Form.Item
                            label={<span className={styles.fieldLabel}>Métodos de pago</span>}
                            name={['dispatch_services', key, 'payment_methods']}
                            className={styles.serviceField}
                            rules={[
                              { required: true, message: 'Selecciona al menos un método de pago' },
                            ]}
                          >
                            <Select
                              mode="multiple"
                              size="middle"
                              allowClear
                              placeholder="Selecciona métodos"
                              options={PAYMENT_METHOD_OPTIONS}
                            />
                          </Form.Item>

                          <Form.Item
                            label={<span className={styles.fieldLabel}>Regla de precio</span>}
                            className={styles.serviceField}
                            style={{ marginBottom: 0 }}
                          >
                            <div className={styles.pricingInputs}>
                              <div className={styles.pricingField}>
                                <span className={styles.pricingFieldLabel}>Condicion</span>
                                <Form.Item
                                  name={['dispatch_services', key, 'operator']}
                                  className={styles.serviceFieldCompact}
                                  rules={[
                                    { required: true, message: 'Selecciona una condicion' }
                                  ]}
                                >
                                  <Select
                                    size="middle"
                                    placeholder="Condicion"
                                    options={PRICE_OPERATOR_OPTIONS}
                                  />
                                </Form.Item>
                              </div>

                              <div className={styles.pricingField}>
                                <span className={styles.pricingFieldLabel}>Valor</span>
                                <Form.Item
                                  name={['dispatch_services', key, 'price']}
                                  className={styles.serviceFieldCompact}
                                  rules={[
                                    { required: true, message: 'Ingresa un valor de precio' },
                                    { type: 'number', min: 0, message: 'Ingresa un valor mayor o igual a 0' }
                                  ]}
                                >
                                  <InputNumber
                                    size="middle"
                                    min={0}
                                    precision={2}
                                    style={{ width: '100%' }}
                                    placeholder="Ej: 5.50"
                                  />
                                </Form.Item>
                              </div>
                            </div>
                          </Form.Item>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item
              label={<span className={styles.label}>ESTADO</span>}
              className={styles.itemColumn}
            >
              <div className={styles.switchContainer}>
                <span className={styles.switchLabel}>{statusLabel}</span>
              </div>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item className={styles.itemSubmit}>
              <Button
                loading={sending}
                className={styles.submit}
                size="large"
                type="primary"
                htmlType="submit">
                {area ? 'ACTUALIZAR ÁREA' : 'CREAR ÁREA'}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default Editor;
